import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loading from "./components/Loading";
import { getConfig } from "./config";
import { buildGuestLoginRedirectOptions, isAuthRecoveryError } from "./utils/authRecovery";

const AuthProvider = ({ children }) => {
  const authConfig = getConfig();
  const domain = authConfig.domain;
  const clientId = authConfig.clientId;
  const audience = authConfig.audience;
  const navigate = useNavigate();

  if (!domain || !clientId || !audience) {
    console.error("Missing Auth0 environment variables. Please check your auth_config.json.");
  }

  const onRedirectCallback = useCallback(
    (appState) => {
      navigate(appState?.returnTo || "/order", { replace: true });
    },
    [navigate]
  );

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin + "/auth/callback",
        audience,
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
};

export const RequireAuth = ({ children }) => {
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently, error } = useAuth0();
  const location = useLocation();
  const loginRequestedRef = useRef(false);
  const [verificationState, setVerificationState] = useState("idle");
  const [verificationError, setVerificationError] = useState(null);

  useEffect(() => {
    let isActive = true;
    const returnTo = location.pathname + location.search;

    const requestLogin = async () => {
      if (loginRequestedRef.current) {
        return;
      }

      loginRequestedRef.current = true;
      if (isActive) {
        setVerificationState("redirecting");
        setVerificationError(null);
      }

      try {
        await loginWithRedirect(buildGuestLoginRedirectOptions(returnTo));
      } catch (loginError) {
        loginRequestedRef.current = false;
        if (isActive) {
          setVerificationError(loginError);
          setVerificationState("error");
        }
      }
    };

    const verifyProtectedAccess = async () => {
      if (isLoading) {
        return;
      }

      if (error) {
        if (isActive) {
          setVerificationError(error);
          setVerificationState("error");
        }
        return;
      }

      if (!isAuthenticated) {
        await requestLogin();
        return;
      }

      if (isActive) {
        setVerificationError(null);
        setVerificationState("checking");
      }

      try {
        await getAccessTokenSilently();
        loginRequestedRef.current = false;
        if (isActive) {
          setVerificationState("ready");
        }
      } catch (tokenError) {
        if (isAuthRecoveryError(tokenError)) {
          await requestLogin();
          return;
        }

        if (isActive) {
          setVerificationError(tokenError);
          setVerificationState("error");
        }
      }
    };

    verifyProtectedAccess();

    return () => {
      isActive = false;
    };
  }, [
    error,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    location.pathname,
    location.search,
    loginWithRedirect,
  ]);

  if (isLoading || verificationState === "checking") {
    return <Loading />;
  }

  if (error || verificationError) {
    return (
      <div className="container-fluid px-3 text-center py-5">
        <div className="alert alert-danger">
          <h5>Authentication Error</h5>
          <p>{(verificationError || error)?.message}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || verificationState === "redirecting" || verificationState !== "ready") {
    return (
      <div className="container-fluid px-3 text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Giriş sayfasına yönlendiriliyorsunuz...</p>
      </div>
    );
  }

  return children;
};

export default AuthProvider;
